import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { updateAnalyticsProperties } from '@app/core/analytics.actions';
import { AnalyticsState } from '@app/core/analytics.reducer';
import { AuthService } from '@app/core/auth.service';
import { MODULE_SERVICE_AREA_PAGE } from '@app/core/mixpanel.constants';
import { UserService } from '@app/core/user.service';
import { StepName } from '@app/registration/enterprise/registration-step-name';
import { OfficeService } from '@app/shared/office.service';

import { ServiceAreaService } from '../../../shared/service-area.service';
import { EnterpriseRegistration } from '../enterprise-registration';
import { EnterpriseRegistrationAnalyticsService } from '../enterprise-registration-analytics.service';
import { RegistrationStep } from '../registration-step';
import { ServiceAreaSelectionStepComponent } from './service-area-selection-step.component';

@Injectable()
export class ServiceAreaConfig extends RegistrationStep {
  GA_LABEL = 'Where_Are_You_Located_Step';

  MODULE = MODULE_SERVICE_AREA_PAGE;
  component = ServiceAreaSelectionStepComponent;
  progress = 50;

  form: UntypedFormGroup = this.formBuilder.group({
    serviceArea: ['', Validators.required],
  });

  constructor(
    private enterpriseRegistrationAnalyticsService: EnterpriseRegistrationAnalyticsService,
    private serviceAreaService: ServiceAreaService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private authService: AuthService,
    private officeService: OfficeService,
    private store: Store<AnalyticsState>,
  ) {
    super();
  }

  initComponent(component: ServiceAreaSelectionStepComponent, state: EnterpriseRegistration) {
    component.b2bCompanyId = state.b2bCompanyId;
    component.form = this.form;
    this.officeService.getRoundedOfficeCount().subscribe(count => {
      component.numberOfOffices = count;
    });
  }

  submit(state: EnterpriseRegistration, captcha: any): Observable<any> {
    if (this.form.valid) {
      const result = this.serviceAreaService.getServiceAreas().pipe(
        take(1),
        switchMap(serviceAreas => {
          const selectedServiceArea = serviceAreas.find(area => area.code === this.form.value.serviceArea);

          state.setServiceArea(selectedServiceArea);
          this.store.dispatch(
            updateAnalyticsProperties({
              updates: {
                service_area: selectedServiceArea.name,
              },
            }),
          );
          this.trackSubmission(state);
          if (state.shouldConvertAccount()) {
            return state.submitAccountConversion(captcha, state.b2bCompany.includesDependent).pipe(take(1));
          } else {
            state.setCurrentStep(StepName.createAccount);
            return observableOf(true);
          }
        }),
      );
      return result;
    }
    return observableThrowError(new Error());
  }

  patchParams(params: Record<string, any>) {
    const { serviceArea } = params;
    if (serviceArea) {
      this.form.patchValue({ serviceArea: serviceArea.code });
      this.shouldFastForward = false;
    }
  }

  private trackSubmission({
    isWhitelisted: isWhitelist,
    serviceArea: { code: serviceArea },
    details: { membershipType: relationshipType },
  }: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.regInputSubmitted({
      isWhitelist,
      serviceArea,
      module: this.MODULE,
      relationshipType,
    });
  }
}
